import { styled } from "@mui/material/styles";
import ErrorIcon from "@mui/icons-material/Error";
import html from "csi/html";
import { isUnreadNotification, NOTIFICATION_STATUS_ERROR, NOTIFICATION_TYPE_SYSTEM, updateNotifications } from "csi/service/notificationService";
import { useNotificationStore } from "csi/service/NotificationStore";
import { Text } from "designSystem/index";
import { black, blue, grey, white, red } from "designSystem/styles/color";
import { throttle } from "lodash";
import React, { FunctionComponent, useCallback, useMemo, useRef } from "react";
import types from "src/csi/types";
import ReadUnreadBtn from "./ReadUnreadBtn";
import SystemAvatar from "Images/SystemAvatar.png";
import { Avatar } from "designSystem/index";
import UserAvatar from "csi/user/UserAvatar";

const PREFIX = "MessageCard";

const classes = {
	messageCard: `${PREFIX}messageCard`,
	messageCardNew: `${PREFIX}messageCardNew`,
	image40: `${PREFIX}image40`,
	infoPane: `${PREFIX}infoPane`,
	date: `${PREFIX}date`,
	secondRow: `${PREFIX}secondRow`,
	thirdRow: `${PREFIX}thirdRow`,
	fourthRow: `${PREFIX}fourthRow`,
	separator: `${PREFIX}separator`,
	error:`${PREFIX}error`,
	title:`${PREFIX}title`,
};

const Root = styled("div")(() => ({
	[`&.${classes.messageCard}`]: {
		height: 103,
		width: 496,
		padding: 12,
		borderBottom: "1px solid rgba(0, 0, 0, .05)",
		boxSizing: "border-box",
		display: "flex",
		flexDirection: "row",
		background: white[50],
		position: "relative",
		"&:hover": {
			background: grey[200],
		},
		"&:active": {
			background: grey[300],
		},
	},

	[`&.${classes.messageCardNew}`]: {
		background: blue[50],
	},

	[`& .${classes.image40}`]: {
		maxHeight: 40,
		maxWidth: 40,
		height: 40,
		width: 40,
		marginRight: 12,
		display: "flex",
		alignItems: "center",
	},

	[`& .${classes.infoPane}`]: {
		display: "flex",
		flexDirection: "column",
		width: 395,
		"& span": {
			minHeight: 18,
		},
	},

	[`& .${classes.date}`]: {
		fontSize: 10,
		verticalAlign: "middle",
		color: grey[800],
		padding: "0 12px",
		fontWeight: 600,
	},

	[`& .${classes.secondRow}`]: {
		marginTop: 6,
		display: "flex",
	},

	[`& .${classes.title}`]: {
		color: black[100],
		fontSize: 12,
		fontWeight: "bold",
		overflow: "hidden",
		display: "-webkit-box",
		WebkitBoxOrient: "vertical",
		WebkitLineClamp: 2,
	},

	[`& .${classes.thirdRow}`]: {
		display: "flex",
	},

	[`& .${classes.error}`]: {
		color: red[600],
		fontSize: 12,
		fontStyle: "italic",
		overflow: "hidden",
		display: "-webkit-box",
		WebkitBoxOrient: "vertical",
		WebkitLineClamp: 2,
	},

}));

export interface MessageCardProps {
	/** notification id */
	id: number;
}

export const MessageCard: FunctionComponent<MessageCardProps> = ({ id }) => {
	const notificationStore = useNotificationStore();
	const { cache, setRead, getInitiatorNode } = notificationStore;

	// Get notification data
	const notification = notificationStore.getById(id);
	const { contextCNL, message, title, initiatorCNL, status, createdAt } = notification;

	// Retrieve context node and derived values
	const contextNode = cache.get(contextCNL);
	const isNew = isUnreadNotification(notification);
	const initiatorNode = getInitiatorNode(initiatorCNL);

	// Parse notification HTML content
	const parsedMessage = html.htmlToText(message);
	const parsedTitle = html.htmlToText(title);

	// Format the notification timestamp
	const date = useMemo(() => types.formatTime(createdAt, types.FORMAT_DATETIME), [contextNode]);

	// Throttle server requests to prevent frequent clicks/DDOS-like behavior
	const updateNotificationsThrottled = useRef(throttle((notificationId, isRead) => {
		updateNotifications([notificationId], isRead);
	}, 1000, { leading: false, trailing: true })).current;

	// Handle click to toggle unread/read status
	const onUnreadClick = useCallback(() => {
		setRead(id, isNew);
		updateNotificationsThrottled(id, isNew);
	}, [id, isNew, updateNotificationsThrottled]);

	// Return early if the contextNode is unavailable
	if (!contextNode) return null;

	// Helper function to render error row (if failed)
	const renderErrorRow = () => {
		if (status !== NOTIFICATION_STATUS_ERROR) return null;
		return (
			<div className={classes.thirdRow}>
				<span className={classes.error}>
					<ErrorIcon
						color="error"
						height="10px"
						sx={{ marginRight: "4px" }}
						width="10px"
					/>
					<span>{parsedMessage}</span>
				</span>
			</div>
		);
	};

	// Helper function to render system avatar (if it's a system notification)
	const renderAvatar = () => {
		if (initiatorNode) {
			return (
				<UserAvatar 
					className={classes.image40}
					user={initiatorNode}
				/>
			);
		}
		return (
			<Avatar 
				className={classes.image40}
				src={SystemAvatar}
			/>
		);
	};

	return (
		<Root className={`csi-notification-card ${classes.messageCard} ${isNew ? classes.messageCardNew : ""}`}>
			{renderAvatar()}
			<div className={classes.infoPane}>
				<div>
					<Text color="textPrimary" text={!initiatorNode ? NOTIFICATION_TYPE_SYSTEM : initiatorNode.$Name} />
					<span className={classes.date}>{date}</span>
				</div>
				<div className={classes.secondRow}>
					<span className={classes.title}>
						<span>{parsedTitle}</span>
					</span>
				</div>
				{renderErrorRow()}
				<ReadUnreadBtn isUnread={isNew} onClick={onUnreadClick} />
			</div>
		</Root>
	);
};
